import { CHAT_HISTORY_LIMIT, type ChatMessage } from './ChatTypes';

export type ChatModelChange =
  | { readonly kind: 'message'; readonly message: ChatMessage }
  | { readonly kind: 'history' | 'clear' };

/** One channel per instance; no optimistic messages or network lifecycle are owned here. */
export class ChatModel {
  readonly channelId: string;
  readonly #listeners = new Set<(change: ChatModelChange) => void>();
  #messages: readonly ChatMessage[] = Object.freeze([]);

  constructor(channelId: string) {
    if (!channelId.trim()) throw new Error('Chat channel ID must not be empty.');
    this.channelId = channelId;
  }

  get messages(): readonly ChatMessage[] {
    return this.#messages;
  }

  /** Returns false for another channel, duplicates, or messages older than retained history. */
  receive(message: ChatMessage): boolean {
    if (!this.#insert(message)) return false;
    const stored = this.#messages.find((item) => item.id === message.id)!;
    this.#notify({ kind: 'message', message: stored });
    return true;
  }

  /** Merge replayed history without presenting it as newly arrived activity. */
  receiveHistory(messages: readonly ChatMessage[]): void {
    let changed = false;
    for (const message of messages) changed = this.#insert(message) || changed;
    if (changed) this.#notify({ kind: 'history' });
  }

  clear(): void {
    this.#messages = Object.freeze([]);
    this.#notify({ kind: 'clear' });
  }

  /** Notifications are synchronous; read messages for the current immutable snapshot. */
  subscribe(listener: (change: ChatModelChange) => void): () => void {
    this.#listeners.add(listener);
    return () => { this.#listeners.delete(listener); };
  }

  #insert(message: ChatMessage): boolean {
    if (message.channelId !== this.channelId) return false;
    if (!message.id || !Number.isSafeInteger(message.sequence) || message.sequence < 0
      || !Number.isFinite(message.createdAt)) throw new Error('Invalid chat message identity or timestamp.');
    if (this.#messages.some((item) => item.id === message.id || item.sequence === message.sequence)) return false;
    if (this.#messages.length === CHAT_HISTORY_LIMIT && message.sequence < this.#messages[0]!.sequence) return false;
    const stored = Object.freeze({
      ...message,
      author: message.author === null ? null : Object.freeze({ ...message.author }),
    });
    this.#messages = Object.freeze([...this.#messages, stored]
      .sort((left, right) => left.sequence - right.sequence)
      .slice(-CHAT_HISTORY_LIMIT));
    return true;
  }

  #notify(change: ChatModelChange): void {
    for (const listener of this.#listeners) listener(change);
  }
}
